import { useState } from 'react';
import { Send } from 'lucide-react';
import { CLOSER, resolvePrompt, pendingQuestions, isRequiredText } from '../utils/caseQuestionnaire';

// The driver-facing interactive questionnaire the office built. Renders the questions still owing
// an answer (photo questions self-hide when there's no photo), fills conditional prompts from
// earlier answers, and submits the set in one go. If the office adds a question after the driver
// has already answered, only the new ones come back as a follow-up round.
export default function CaseQuestionnaire({ c, busy, onSubmit, extra }) {
  const hasPhoto = (c.images || []).length > 0;
  const prior = ((c.questionnaire_answers || {}).items) || [];
  const followUp = prior.length > 0;
  // The closer is re-asked each round under its own id, so a later "No" can't overwrite the detail
  // the driver gave the first time.
  const closer = followUp ? { ...CLOSER, id: `${CLOSER.id}_${prior.filter((a) => String(a.id).startsWith(CLOSER.id)).length + 1}` } : CLOSER;
  const steps = [...pendingQuestions(c), closer];
  const [ans, setAns] = useState({});
  const set = (id, patch) => setAns((a) => ({ ...a, [id]: { ...(a[id] || {}), ...patch } }));

  const ready = steps.every((q) => {
    const a = ans[q.id] || {};
    if (q.type === 'text') return !isRequiredText(q) || !!(a.value || '').trim(); // office-typed: required
    if (q.type === 'yesno' || q.type === 'choice') return a.value != null && a.value !== '';
    if (q.type === 'yesno_text') return a.value === 'No' || (a.value === 'Yes' && (a.text || '').trim());
    return true;
  });

  const submit = () => onSubmit(steps.map((q) => {
    const a = ans[q.id] || {};
    let answer = '';
    if (q.type === 'yesno' || q.type === 'choice') answer = a.value || '';
    else if (q.type === 'text') answer = (a.value || '').trim();
    else if (q.type === 'yesno_text') answer = a.value === 'Yes' ? (a.text || '').trim() : 'No';
    return { id: q.id, prompt: resolvePrompt(q.prompt, ans), type: q.type, answer };
  }));

  const btn = (active) => ({ padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', border: `1px solid ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: active ? 'rgba(212,175,55,0.15)' : 'transparent', color: 'var(--hp-text)' });
  const chip = (active) => ({ ...btn(active), borderRadius: 20, padding: '8px 14px', fontSize: '0.82rem' });
  const ta = { width: '100%', minHeight: 44, marginTop: 6, background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem', fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, border: '1px solid var(--hp-gold)', borderRadius: 10, padding: 14, background: 'rgba(201,168,76,0.05)' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{followUp ? 'The office has asked something further' : 'A few questions from the office'}</div>
      {steps.map((q, i) => {
        const a = ans[q.id] || {};
        return (
          <div key={q.id}>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 8 }}>{i + 1}. {resolvePrompt(q.prompt, ans)}</div>
            {q.requiresPhoto && hasPhoto && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {c.images.map((src, k) => <a key={k} href={src} target="_blank" rel="noreferrer"><img src={src} alt="delivery" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} /></a>)}
              </div>
            )}
            {(q.type === 'yesno' || q.type === 'yesno_text') && (
              <div style={{ display: 'flex', gap: 8 }}>
                {['Yes', 'No'].map((v) => <button key={v} onClick={() => set(q.id, { value: v })} style={btn(a.value === v)}>{v}</button>)}
              </div>
            )}
            {q.type === 'yesno_text' && a.value === 'Yes' && (
              <textarea value={a.text || ''} onChange={(e) => set(q.id, { text: e.target.value })} placeholder="Type the details" style={ta} />
            )}
            {q.type === 'choice' && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(q.options || []).map((opt) => <button key={opt} onClick={() => set(q.id, { value: opt })} style={chip(a.value === opt)}>{opt}</button>)}
              </div>
            )}
            {q.type === 'text' && (
              <textarea value={a.value || ''} onChange={(e) => set(q.id, { value: e.target.value })} placeholder={isRequiredText(q) ? 'Reply to the office — needed before you can submit' : 'Type your answer'} style={ta} />
            )}
          </div>
        );
      })}
      {extra /* e.g. the optional photo field — sits with the answers, above Submit */}
      <button disabled={busy || !ready} onClick={submit} style={{ padding: 12, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.55, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Send size={14} /> Submit answers</button>
    </div>
  );
}
